import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { SOLUTIONS } from "@/config/site";

const EASE = [0.22, 1, 0.36, 1];

function PanelContent({ s, testPrefix }) {
  return (
    <div className="flex h-full flex-col">
      <img
        src={s.image}
        alt={s.imageAlt}
        loading="lazy"
        className="h-52 w-full shrink-0 object-cover sm:h-56"
        data-testid={`${testPrefix}-panel-image`}
      />
      <div className="flex flex-1 flex-col gap-4 p-6 lg:p-8">
        <div>
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.2em] text-cobalt">
            Destination {s.num}
          </p>
          <h3 className="mt-2 font-display text-xl font-bold text-ink sm:text-2xl">{s.name}</h3>
        </div>
        <p className="text-[15px] leading-relaxed text-ink/75">{s.atlasWhat}</p>
        <div className="space-y-2.5 text-sm">
          <p className="flex gap-2">
            <span className="shrink-0 font-display font-semibold text-ink">Who it's for:</span>
            <span className="text-ink/70">{s.audience}</span>
          </p>
          <p className="flex gap-2">
            <span className="shrink-0 font-display font-semibold text-ink">Might help improve:</span>
            <span className="text-ink/70">{s.helps}</span>
          </p>
        </div>
        <div className="mt-auto flex flex-wrap gap-3 pt-3">
          <Link
            to={`/solutions/${s.slug}`}
            data-testid="atlas-cta-button"
            className="group inline-flex items-center gap-2 rounded-md bg-cobalt px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-midnight"
          >
            View Solution
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <a
            href={s.cta.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="atlas-visit-provider"
            className="inline-flex items-center gap-2 rounded-md border border-silver bg-white px-5 py-2.5 font-display text-sm font-semibold text-ink transition-colors hover:border-cobalt hover:text-cobalt"
          >
            Visit {s.provider}
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Atlas() {
  const [active, setActive] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(0);
  const tabRefs = useRef([]);
  const s = SOLUTIONS[active];

  const onKeyDown = (e) => {
    let n = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") n = (active + 1) % SOLUTIONS.length;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") n = (active - 1 + SOLUTIONS.length) % SOLUTIONS.length;
    if (e.key === "Home") n = 0;
    if (e.key === "End") n = SOLUTIONS.length - 1;
    if (n !== null) {
      e.preventDefault();
      setActive(n);
      tabRefs.current[n]?.focus();
    }
  };

  return (
    <section id="atlas" className="border-y border-silver/40 bg-white py-16 sm:py-24 lg:py-28" data-testid="atlas-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cobalt">
            Signature Section
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            The Opportunity Atlas
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink/75">
            Seven service destinations, each one a specialized provider. Choose where your business
            needs support — the atlas keeps its shape while you explore.
          </p>
        </div>

        <div className="mt-10 hidden lg:grid lg:grid-cols-12 lg:gap-8 lg:items-stretch" data-testid="atlas-desktop">
          <div role="tablist" aria-label="Solution destinations" className="col-span-5 flex flex-col">
            {SOLUTIONS.map((item, i) => (
              <button
                key={item.id}
                role="tab"
                aria-selected={i === active}
                aria-controls="atlas-panel"
                tabIndex={i === active ? 0 : -1}
                ref={(el) => (tabRefs.current[i] = el)}
                onClick={() => setActive(i)}
                onKeyDown={onKeyDown}
                data-testid={`atlas-destination-item-${i}`}
                className={`group flex flex-1 items-center gap-5 border-t px-5 py-5 text-left transition-all duration-300 first:border-t-0 ${
                  i === active
                    ? "border-cobalt bg-platinum"
                    : "border-silver/40 hover:border-cobalt/50 hover:bg-platinum/60"
                } ${i === SOLUTIONS.length - 1 ? "border-b" : ""}`}
              >
                <span
                  className={`font-display text-sm font-bold transition-colors ${
                    i === active ? "text-cobalt" : "text-ink/40"
                  }`}
                >
                  {item.num}
                </span>
                <span className="flex-1">
                  <span
                    className={`block font-display text-[15px] font-semibold transition-colors ${
                      i === active ? "text-cobalt" : "text-ink"
                    }`}
                  >
                    {item.name}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink/55">{item.menuLabel}</span>
                </span>
                <ArrowRight
                  className={`h-4 w-4 transition-all ${
                    i === active ? "translate-x-0 text-cobalt opacity-100" : "-translate-x-1 opacity-0"
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="col-span-7" id="atlas-panel" role="tabpanel" aria-label={s.name}>
            <AnimatePresence mode="wait">
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.28, ease: EASE }}
                className="h-full overflow-hidden rounded-md border border-silver/60 bg-platinum shadow-sm"
                data-testid="atlas-panel"
              >
                <PanelContent s={s} testPrefix="atlas" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-8 lg:hidden" data-testid="atlas-mobile">
          <div className="divide-y divide-silver/50 border-y border-silver/50">
            {SOLUTIONS.map((item, i) => (
              <div key={item.id}>
                <button
                  type="button"
                  aria-expanded={mobileOpen === i}
                  data-testid={`atlas-accordion-item-${i}`}
                  onClick={() => setMobileOpen(mobileOpen === i ? -1 : i)}
                  className="flex w-full items-center gap-4 py-4 text-left"
                >
                  <span className="font-display text-sm font-bold text-cobalt">{item.num}</span>
                  <span className="flex-1 font-display text-[15px] font-semibold text-ink">
                    {item.name}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-cobalt transition-transform duration-300 ${
                      mobileOpen === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {mobileOpen === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6">
                        <PanelContent s={item} testPrefix={`atlas-acc-${i}`} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
